import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'

import { fetchFeed, setAttr } from '../redux/feedInfiniteSlice'
import { deleteMyDecor } from '../redux/apis'

import Detail from '../components/DesignerCommon/Detail'
import Feed from '../components/DesignerFeed/Feed'
import CropMyDecorModal from '../components/DesignerModals/CropMyDecorModal'
import RemoveBGMyDecorModal from '../components/DesignerModals/RemoveBGMyDecorModal'
import DesignUploadMyRoomCard from '../components/DesignerDesigns/cards/DesignUploadMyRoomCard'
import DeleteConfirmation from '../components/DesignerModals/DeleteConfirmation'
import FilterBar from '../components/DesignerWorkspace/tray/filter/MyDecorFilterBar'
import AdvanceFilterList from '../components/DesignerWorkspace/tray/lists/AdvanceFilterList'
import MyDecorCard from '../components/DesignerWorkspace/cards/MyDecorCard'

import { FEED_RESET } from '../redux/constants'
import { getMembershipType } from '../utils/getMembershipType'

const FilterContainer = styled.div`
  position: relative;
  margin-left: auto;
  width: 30%;
  margin-top: -45px;
  margin-bottom: 45px;

  & > div {
    flex-direction: row-reverse;
    margin: 0;
  }

  @media screen and (max-width: 750px) {
    margin-top: -37px;
    margin-bottom: 20px;
  }
`

const PopupContainer = styled.div`
  position: absolute;
  right: 0;
  top: 30px;
  z-index: 100;
  width: 285px;

  & > div {
    max-height: 400px;
    overflow-y: auto;
  }
`

const CardContainer = styled.div`
  height: 296px;
  width: 234px;

  @media screen and (max-width: 750px) {
    width: calc(50% - 5px);
    height: 50vw;
  }
`

const FeedContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 10px;
`

const Card = props => {
  return (
    <CardContainer>
      <MyDecorCard {...props} size="large" />
    </CardContainer>
  )
}

const AddCard = props => {
  return (
    <CardContainer>
      <DesignUploadMyRoomCard {...props} />
    </CardContainer>
  )
}

const MyRooms = () => {
  const dispatch = useDispatch()
  const {
    feed: mydecor,
    loading,
    error,
    root
  } = useSelector(state => state.mydecor)
  const profile = useSelector(state => state.profile.me)
  const membershipInfo = getMembershipType(profile)

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [disableUpload, setDisableUpload] = useState(false)
  const [toDelete, setToDelete] = useState()
  const [isSelectVisible, setSelectVisible] = useState(false)
  const [showCrop, setShowCrop] = useState(false)
  const [toCrop, setToCrop] = useState(null)
  const [showRemoveBg, setShowRemoveBg] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('all')

  useEffect(() => {
    if (membershipInfo.isMember) setDisableUpload(false)
    else setDisableUpload(true)
  }, [root, membershipInfo.isMember])

  const resetFeed = () => {
    window.scrollTo(0, 0)
    dispatch(fetchFeed('mydecor')({ status: FEED_RESET }))
  }

  const handleFetchMore = () => {
    dispatch(fetchFeed('mydecor')({ status: null }))
  }

  useEffect(resetFeed, [dispatch])

  const onCategorySelect = category => {
    setSelectedCategory(category.name)
    dispatch(
      setAttr('mydecor')({
        addition:
          category.name === 'all'
            ? {}
            : {
                category: category.name
              }
      })
    )
    setSelectVisible(false)
    resetFeed()
  }

  const handleDeleteToConfirm = data => {
    setShowDeleteModal(true)
    setToDelete(data)
  }

  const handleDelete = () => {
    if (!toDelete) return

    if (!toDelete.objectId) return

    deleteMyDecor(toDelete.objectId).then(() => {
      resetFeed()
      setShowDeleteModal(false)
    })
  }

  const handleRemoveBgComplete = image => {
    const url = URL.createObjectURL(image)
    setToCrop(url)
  }

  const handleCropClose = e => {
    setToCrop(null)
    setShowCrop(false)
  }

  const handleRemoveBgClose = e => {
    setToCrop(null)
    setShowRemoveBg(false)
  }

  const handleCrop = img => {
    setToCrop(img)
    setShowCrop(true)
  }

  return (
    <>
      <DeleteConfirmation
        show={showDeleteModal}
        title="Delete My Decor?"
        onClose={e => setShowDeleteModal(false)}
        onDelete={handleDelete}
      >
        Are you sure you want to delete this MyDecor?
      </DeleteConfirmation>
      <CropMyDecorModal
        show={showCrop}
        toCrop={toCrop}
        onRemoveBGComplete={handleRemoveBgComplete}
        onComplete={handleCropClose}
        category={selectedCategory}
        onClose={handleCropClose}
      />
      <RemoveBGMyDecorModal
        show={showRemoveBg}
        toCrop={toCrop}
        isMember={membershipInfo.isMember}
        onRemoveBGComplete={handleRemoveBgClose}
        onComplete={handleRemoveBgClose}
        onClose={handleRemoveBgClose}
        category={selectedCategory}
        withOverlay
      />
      <Detail title="Upload MyDecor" backToRoot>
        <FilterContainer>
          <FilterBar
            filters={[]}
            isSelectVisible={false}
            toggleSelectVisible={setSelectVisible}
            onSearchBlur={() => {}}
            onSearchFocus={() => {}}
            onPillClick={() => {}}
          />
          <PopupContainer>
            <AdvanceFilterList
              style={{
                visibility: isSelectVisible ? 'visible' : 'hidden',
                height: isSelectVisible ? 'auto' : '0'
              }}
              isVisible={isSelectVisible}
              handleClose={() => setSelectVisible(false)}
              prependCategories={[
                { shortName: 'All', name: 'all', iconName: 'all1.svg' }
              ]}
              hideSort
              isTabsVisible={false}
              isSubcategoryAllowed={false}
              onOptionClick={onCategorySelect}
            />
          </PopupContainer>
        </FilterContainer>
        <FeedContainer>
          <AddCard disable={disableUpload} onCrop={handleCrop} />
          <Feed
            data={mydecor}
            onDelete={handleDeleteToConfirm}
            onFetch={handleFetchMore}
            card={Card}
            // loading={loading}
            error={error}
            from="/mydecor"
            end={true}
            selectCategoryForRecentlyCreated={selectedCategory === 'all'}
          />
        </FeedContainer>
      </Detail>
    </>
  )
}

export default MyRooms
